import { useApiaries } from '@/contexts/useApiaries'
import {
  Combobox,
  Field,
  HStack,
  TagsInput,
  createListCollection,
  useCombobox,
  useTagsInput,
} from '@chakra-ui/react'
import { useEffect, useId, useMemo, useRef } from 'react'
import { useTranslation } from 'react-i18next'

interface DeploymentLabelComboboxProps {
  value?: string | null
  onChange?: (value: string | null) => void
  error?: string
  required?: boolean
  label?: string
  id?: string
  disabled?: boolean
  onBlur?: () => void
}

export const DeploymentLabelCombobox = ({
  value,
  onChange,
  error,
  required = false,
  label,
  id = 'deployment-label',
  disabled = false,
  onBlur,
}: DeploymentLabelComboboxProps) => {
  const { t } = useTranslation(undefined, { useSuspense: false })
  const { deploymentLabels, isLoading } = useApiaries()

  const defaultLabel = label ?? t('fields.deploymentLabel')

  // Memoized collection recreated only when deploymentLabels array changes
  const collection = useMemo(
    () =>
      createListCollection({
        items: deploymentLabels.map((label) => label.value),
      }),
    [deploymentLabels],
  )

  const inputId = useId()
  const controlRef = useRef<HTMLDivElement | null>(null)

  const tags = useTagsInput({
    ids: { input: inputId },
    value: value ? [value] : [],
    onValueChange: (details) => {
      // Take the last value (most recently added/changed)
      const newValue = details.value.at(-1)
      onChange?.(newValue ?? null)
    },
  })

  const combobox = useCombobox({
    ids: { input: inputId },
    collection,
    positioning: {
      getAnchorRect() {
        return (
          controlRef.current?.getBoundingClientRect() ?? new DOMRect(0, 0, 0, 0)
        )
      },
    },
    value: [],
    allowCustomValue: true,
    onValueChange: (e) => {
      const newValue = e.value[0]
      if (newValue && newValue !== tags.value[0]) {
        // Replace existing value with new one
        tags.setValue([newValue])
      }
    },
    selectionBehavior: 'clear',
    openOnClick: true,
    disabled,
    invalid: !!error,
  })

  // Sync external value changes to tags input
  const previousValueRef = useRef(value)

  // biome-ignore lint/correctness/useExhaustiveDependencies: tags methods are stable and including them causes infinite loops
  useEffect(() => {
    const newValueStr = value ?? ''
    // Only update if the external value prop actually changed
    if (previousValueRef.current === newValueStr) {
      return
    }

    previousValueRef.current = newValueStr

    if (newValueStr && newValueStr !== tags.value[0]) {
      tags.setValue([newValueStr])
    } else if (!newValueStr && tags.value.length > 0) {
      tags.clearValue()
    }
  }, [value])

  const isInvalid = Boolean(error)

  return (
    <Field.Root required={required} invalid={isInvalid}>
      <Combobox.RootProvider value={combobox}>
        <TagsInput.RootProvider value={tags}>
          <TagsInput.Label htmlFor={id}>
            {defaultLabel}
            <Field.RequiredIndicator ml={1} />
          </TagsInput.Label>

          <TagsInput.Control
            ref={controlRef}
            borderColor={isInvalid ? 'content.error' : 'border'}
          >
            <HStack opacity={disabled ? 0.5 : 1}>
              {tags.value.map((tag, index) => (
                <TagsInput.Item
                  key={tag}
                  index={index}
                  value={tag}
                  disabled={disabled}
                >
                  <TagsInput.ItemPreview>
                    <TagsInput.ItemText>{tag}</TagsInput.ItemText>
                    <TagsInput.ItemDeleteTrigger />
                  </TagsInput.ItemPreview>
                </TagsInput.Item>
              ))}

              <Combobox.Input unstyled asChild>
                {!disabled && (
                  <TagsInput.Input
                    placeholder={
                      isLoading
                        ? String(t('deploymentLabelCombobox.loading'))
                        : String(t('deploymentLabelCombobox.placeholder'))
                    }
                    disabled={isLoading || disabled}
                    onBlur={onBlur}
                  />
                )}
              </Combobox.Input>
            </HStack>
          </TagsInput.Control>

          <Combobox.Positioner>
            <Combobox.Content>
              <Combobox.Empty>
                {t('deploymentLabelCombobox.noLabelsFound')}
              </Combobox.Empty>
              {collection.items.map((item) => (
                <Combobox.Item item={item} key={item}>
                  <Combobox.ItemText>{item}</Combobox.ItemText>
                  <Combobox.ItemIndicator />
                </Combobox.Item>
              ))}
            </Combobox.Content>
          </Combobox.Positioner>
        </TagsInput.RootProvider>
      </Combobox.RootProvider>
      {error && (
        <Field.ErrorText position="absolute" bottom="-1.2rem">
          {error}
        </Field.ErrorText>
      )}
    </Field.Root>
  )
}
